import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { ApplicationSdk } from '../../../../core/sdk';
import { formatName, formatStringToDate } from '../../../../utils/helper';
import { MatenRequestResponse } from '../../../../shared';

interface RequestType {
    id: number;
    name: string;
    description: string;
    requires_approval: boolean;
}

interface User {
    id: number;
    name: string;
    email: string;
}

const formatDate = (date: Date | null) => {
    if (!date) return null;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Monate sind 0-basiert
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
};

/**
 * The dialog store for request editing
 */
class RequestDialogStore {
    private sdk: ApplicationSdk;

    public state: string = 'edit'; // Only edit mode for now
    public request_id: number | null = null;

    // Request data
    public user_id: number | null = null;
    public employee_name: string = '';
    public request_type_id: number | null = null;
    public request_type_name: string = '';
    public start_date: any = null;
    public end_date: any = null;
    public comment: string = '';
    public status: string = '';
    public approver_id: number | null = null;
    public approver_name: string = '';
    public approver_comment: string = '';
    public approved: number | null = null;
    public created: number | null = null;
    public updated: number | null = null;

    // Filter
    public request_employee_filter: string = '';
    public request_employee: any = null;

    // Loading states
    public requestDataLoading: boolean = false;
    public saving: boolean = false;

    // Available data for dropdowns
    public availableRequestTypes: RequestType[] = [];
    public availableUsers: User[] = [];

    constructor(sdk: ApplicationSdk) {
        makeObservable(this, {
            state: observable,
            request_id: observable,
            user_id: observable,
            employee_name: observable,
            request_type_id: observable,
            request_type_name: observable,
            start_date: observable,
            end_date: observable,
            comment: observable,
            status: observable,
            approver_id: observable,
            approver_name: observable,
            approver_comment: observable,
            approved: observable,
            created: observable,
            updated: observable,
            requestDataLoading: observable,
            saving: observable,
            availableRequestTypes: observable,
            availableUsers: observable,

            // Employee Filter
            currentEmployeeFilter: computed,
            selectedEmployees: computed,
            request_employee_filter: observable,
            filterEmployees: action,
            selectEmployee: action,
            request_employee: observable,

            // Actions
            fetchRequest: action,
            onUpdateRequest: action,
            resetForm: action,
            setComment: action,
            setRequestType: action,
            setUser: action,
            init: action,

            // Computed
            canSave: computed,
            isValidDateRange: computed,
            isStartDateValid: computed,
            isEndDateValid: computed,
            isNewRequest: computed,
        });

        this.sdk = sdk;
    }

    // FILTER
    public filterEmployees(filter: string): void {
        this.request_employee_filter = filter;
    }

    public selectEmployee(employee: any): void {
        this.request_employee = employee || null;
    }

    get currentEmployeeFilter(): string {
        return this.request_employee_filter;
    }

    get selectedEmployees(): any {
        return this.request_employee;
    }

    get id(): string {
        return 'request';
    }

    get isNewRequest(): boolean {
        return this.request_id === null;
    }

    get canSave(): boolean {
        return (
            !this.saving &&
            !!this.request_type_id &&
            !!this.start_date &&
            !!this.end_date &&
            this.isValidDateRange &&
            ((this.isNewRequest && this.request_employee) || !this.isNewRequest)
        );
    }

    get isValidDateRange(): boolean {
        if (!this.start_date || !this.end_date) return true;
        return new Date(this.start_date) <= new Date(this.end_date);
    }

    public async init(config: any): Promise<void> {
        this.request_id = config?.request_id || null;
        let promises: any = [];

        if (!this.request_id) {
            promises.push(this.sdk.employee?.fetch());
        }

        promises.push(this.fetchRequest(this.request_id || null));

        if (promises.length) {
            await Promise.all(promises);
        }

        runInAction(() => {
            if (config.employee_id) {
                this.request_employee =
                    this.sdk.employee?.all.data.find(
                        (employee: any) => employee.id === config.employee_id
                    )?.id || null;
            }
        });
    }

    get isStartDateValid(): boolean {
        return !!this.start_date && this.isValidDateRange;
    }

    get isEndDateValid(): boolean {
        return !!this.end_date && this.isValidDateRange;
    }

    set startDate(date: any) {
        runInAction(() => {
            this.start_date = date || null;
        });
    }

    set endDate(date: any) {
        runInAction(() => {
            this.end_date = date || null;
        });
    }

    public setComment(comment: string): void {
        this.comment = comment;
    }

    public setRequestType(requestTypeId: number): void {
        this.request_type_id = requestTypeId;
        const requestType = this.availableRequestTypes.find(rt => rt.id === requestTypeId);
        if (requestType) {
            this.request_type_name = requestType.name;
        }
    }

    public setUser(userId: number): void {
        this.user_id = userId;
        const user = this.availableUsers.find(u => u.id === userId);
        if (user) {
            this.employee_name = user.name;
        }
    }

    public resetForm(): void {
        this.request_id = null;
        this.user_id = null;
        this.employee_name = '';
        this.request_type_id = null;
        this.request_type_name = '';
        this.start_date = '';
        this.end_date = '';
        this.comment = '';
        this.status = '';
        this.approver_id = null;
        this.approver_name = '';
        this.approver_comment = '';
        this.approved = null;
        this.created = null;
        this.updated = null;
    }

    public async fetchRequest(requestId: number | null): Promise<void> {
        this.requestDataLoading = true;
        try {
            const response = await this.sdk.fetchApi(
                'request/manager',
                this.isNewRequest ? 'new' : 'get',
                {
                    request_id: requestId ? requestId : undefined,
                }
            );

            if (response?.status === 200) {
                runInAction(() => {
                    const { request, requestTypes } = response.json;

                    if (request) {
                        this.request_id = request.id;
                        this.user_id = request.user_id;
                        this.employee_name = request.employee
                            ? formatName(request.employee.firstname, request.employee.lastname)
                            : '-';
                        this.request_type_id = request.request_type.id;
                        this.request_type_name = request.request_type.name;
                        this.start_date = formatStringToDate(request.start_date_formatted);
                        this.end_date = formatStringToDate(request.end_date_formatted);
                        this.comment = request.comment || '';
                        this.status = request.status;
                        this.approver_id = request.approver_id;
                        this.approver_name = request.approver
                            ? formatName(request.approver.firstname, request.approver.lastname)
                            : '-';
                        this.approver_comment = request.approver_comment || '';
                        this.approved = request.approved_utc;
                        this.created = request.created_utc;
                        this.updated = request.updated_utc;
                    }

                    // Set available data
                    this.availableRequestTypes = requestTypes || [];
                });
            } else {
                this.sdk.showErrorToast('Fehler beim Laden des Antrags');
            }
        } catch (error) {
            console.error('Error fetching request:', error);
            this.sdk.showErrorToast('Fehler beim Laden des Antrags');
        } finally {
            runInAction(() => {
                this.requestDataLoading = false;
            });
        }
    }

    public async onCreateRequest(): Promise<MatenRequestResponse> {
        const response = await this.sdk.fetchApi('request/manager', 'add', {
            employee_id: this.request_employee,
            request_type_id: this.request_type_id,
            start_date: formatDate(this.start_date),
            end_date: formatDate(this.end_date),
            comment: this.comment,
        });

        console.log(response);

        if (response?.status === 201) {
            return { success: true, status: 201 };
        }

        return { success: false, status: response.status, error: 'ERR_UNKNOWN' };
    }

    public async onSaveRequest(): Promise<MatenRequestResponse> {
        if (this.isNewRequest) {
            return this.onCreateRequest();
        }

        return this.onUpdateRequest();
    }

    public async onUpdateRequest(): Promise<MatenRequestResponse> {
        if (!this.canSave || !this.request_id)
            return { success: false, status: 400, error: 'ERR_INVALID_REQUEST' };

        this.saving = true;
        try {
            const response = await this.sdk.fetchApi('request/manager', 'update', {
                request_id: this.request_id,
                user_id: this.user_id,
                request_type_id: this.request_type_id,
                start_date: formatDate(this.start_date),
                end_date: formatDate(this.end_date),
                comment: this.comment,
            });

            if (response?.status === 200) {
                this.sdk.showSuccessToast('Antrag erfolgreich aktualisiert');
                return { success: true, status: 200 };
            } else {
                this.sdk.showErrorToast(
                    response.message || 'Fehler beim Aktualisieren des Antrags'
                );
                return { success: false, status: response.status };
            }
        } catch (error) {
            console.error('Error updating request:', error);
            this.sdk.showErrorToast('Fehler beim Aktualisieren des Antrags');
            return { success: false, status: 500, error: 'ERR_UNKNOWN' };
        } finally {
            runInAction(() => {
                this.saving = false;
            });
        }
    }

    public async onApproveRequest(comment: string = ''): Promise<MatenRequestResponse> {
        const response = await this.sdk.fetchApi('request/manager', 'approve', {
            request_id: this.request_id,
            approver_comment: comment,
        });

        if (response.status === 200) {
            return { success: true, status: 200 };
        }

        return { success: false, status: response.status };
    }

    public async onRejectRequest(comment: string = ''): Promise<MatenRequestResponse> {
        const response = await this.sdk.fetchApi('request/manager', 'reject', {
            request_id: this.request_id,
            approver_comment: comment,
        });

        if (response.status === 200) {
            return { success: true, status: 200 };
        }

        return { success: false, status: response.status };
    }

    public async fetchUsers(): Promise<void> {
        try {
            // This endpoint should be implemented in the backend
            const response = await this.sdk.fetchApi('user/manager', 'list', {});
            if (response.success) {
                runInAction(() => {
                    this.availableUsers = response.users || [];
                });
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    }
}

export default RequestDialogStore;
